import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import NotificationsIcon from '@mui/icons-material/Notifications';

import styles from './NotificationDropdown.module.scss';
import CircularProgress from '@mui/material/CircularProgress';
import { useNotifications } from '../../../hooks/useNotifications';
import { NotificationItem } from '../../../types';
import { usePushContext } from '../../../context/PushNotificationContext';
import { useChat } from '../../../context/ChatContext';

interface NotificationDropdownProps {
    customButtonClass?: string;
    children?: React.ReactNode;
    onClick?: () => void;
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ customButtonClass, children, onClick }) => {
    const { t } = useTranslation();
    const { unreadCount, refreshTrigger } = usePushContext();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const { toggleChatDrawer, setCurrentConversationId } = useChat();

    const {
        recentNotifications,
        recentLoading,
        recentError,
        fetchRecentNotifications,
        markAllNotificationsAsRead,
        markNotificationAsRead,
        markAsReadLoading
    } = useNotifications();

    useEffect(() => {
        fetchRecentNotifications();
    }, [fetchRecentNotifications, unreadCount, refreshTrigger]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const getNotificationIcon = (type: string) => {
        switch (type) {
            case 'BOOKING': return <span className="bw-menu-icon">✓</span>;
            case 'REMINDER': return <span className="bw-menu-icon">⏰</span>;
            case 'PAYMENT': return <span className="bw-menu-icon">$</span>;
            case 'SYSTEM': return <span className="bw-menu-icon">!</span>;
            default: return <span className="bw-menu-icon">🔔</span>;
        }
    };

    const handleViewAll = () => {
        setIsOpen(false);
        navigate('/notifications');
    };

    const handleNavigationByType = (notification: NotificationItem) => {
        const type = notification.type.toUpperCase();
        const data = notification.data || {};

        switch (type) {
            case 'PAYMENT':
                navigate('/payment-list');
                break;
            case 'BOOKING':
                if (data.professional_uid) {
                    navigate(`/professionals/booking-details/${data.professional_uid}?booking_id=${data.booking_id}`);
                }
                break;
            case 'REMINDER':
                if (data.professional_uid) {
                    navigate(`/professionals/booking-details/${data.professional_uid}?booking_id=${data.booking_id}`);
                }
                break;
            case 'CHAT':
                if (data.conversation_id || data.conversationId) {
                    setCurrentConversationId(Number(data.conversation_id || data.conversationId));
                }
                setIsOpen(false);
                toggleChatDrawer();
                break;
            case 'SYSTEM':
                if (data.conversationId || data.conversation_id) {
                    setCurrentConversationId(Number(data.conversation_id || data.conversationId));
                    setIsOpen(false);
                    toggleChatDrawer();
                }
                break;
        }
    };

    const handleNotificationClick = (notification: NotificationItem) => {
        if (notification.unread) {
            markNotificationAsRead(notification.uid);
        }
        handleNavigationByType(notification);
    };

    return (
        <div ref={menuRef} style={{ position: 'relative', display: 'inline-block' }}>
            <button
                className={classNames('bw-header-icon', { 'is-active': isOpen }, customButtonClass)}
                onClick={() => {
                    if (onClick) {
                        onClick();
                        return;
                    }
                    setIsOpen(!isOpen);
                }}
                aria-label="Notificaciones"
                aria-expanded={isOpen}
            >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M18 9a6 6 0 0 0-12 0c0 7-3 7-3 8h18c0-1-3-1-3-8"></path>
                    <path d="M10 20h4"></path>
                </svg>
                {unreadCount > 0 && (
                    <span className="bw-header-badge">
                        {unreadCount}
                    </span>
                )}
                {children}
            </button>

            {isOpen && (
                <div className="bw-header-menu">
                    <div className="bw-header-menu-title">
                        <strong>{t("Notifications.title", "Notificaciones")}</strong>
                        <span>
                            {unreadCount > 0 
                                ? t('Notifications.unreadCount', '{{count}} nuevas', { count: unreadCount }) 
                                : t('Notifications.noUnreadCount', '0 nuevas')}
                        </span>
                    </div>

                    {recentLoading ? (
                        <div style={{ padding: '20px', textAlign: 'center', fontSize: '10px' }}>
                            {t('landing.NotificationDropdown.loading', 'Cargando...')}
                        </div>
                    ) : recentError ? (
                        <div style={{ padding: '20px', textAlign: 'center', fontSize: '10px' }}>
                            {t('landing.NotificationDropdown.errorLoading', 'Error al cargar')}
                        </div>
                    ) : recentNotifications.length > 0 ? (
                        recentNotifications.map((notification) => (
                            <button
                                key={notification.uid}
                                className="bw-menu-item"
                                type="button"
                                onClick={() => handleNotificationClick(notification)}
                            >
                                <span className="bw-menu-icon">
                                    {notification.type === 'BOOKING' ? '✓' : notification.type === 'REMINDER' ? 'SH' : 'M'}
                                </span>
                                <span className="bw-menu-copy">
                                    <strong>{notification.title}</strong>
                                    <span>{notification.message}</span>
                                </span>
                                {notification.unread && <span className="bw-unread"></span>}
                            </button>
                        ))
                    ) : (
                        <div style={{ padding: '20px', textAlign: 'center', fontSize: '10px' }}>
                            {t("Notifications.empty", "No tienes notificaciones")}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};